import { createQuestionPage } from './questionnairePages';
import {
  DEFAULT_SECTION_LINES,
  DEFAULT_CUSTOM_PAGE_PRESETS,
  PAGE_TYPE_BASIS,
  PAGE_TYPE_FACH
} from './questionnaireEditorConstants';

export const cloneQuestions = (questions = []) => {
  try {
    return JSON.parse(JSON.stringify(Array.isArray(questions) ? questions : []));
  } catch (error) {
    return Array.isArray(questions) ? [...questions] : [];
  }
};

export const getConfiguredTemplateId = (storageKey) => {
  if (typeof window === 'undefined') return '';
  try {
    return String(window.localStorage.getItem(storageKey) || '').trim();
  } catch (error) {
    console.warn('Could not read configured template id:', error);
    return '';
  }
};

export const setConfiguredTemplateId = (storageKey, templateId) => {
  if (typeof window === 'undefined') return false;
  try {
    window.localStorage.setItem(storageKey, String(templateId || '').trim());
    return true;
  } catch (error) {
    console.warn('Could not persist configured template id:', error);
    return false;
  }
};

export const clearConfiguredTemplateId = (storageKey) => {
  if (typeof window === 'undefined') return false;
  try {
    window.localStorage.removeItem(storageKey);
    return true;
  } catch (error) {
    console.warn('Could not clear configured template id:', error);
    return false;
  }
};

export const normalizeCustomPageType = (value, fallback = PAGE_TYPE_FACH) => {
  const normalized = String(value || '').trim().toLowerCase();
  if (normalized === PAGE_TYPE_BASIS || normalized === PAGE_TYPE_FACH) return normalized;
  return fallback;
};

export const inferCustomPageTypeByIndex = (index) => (index === 0 ? PAGE_TYPE_BASIS : PAGE_TYPE_FACH);

export const getPageTypeLabel = (pageType) => (
  normalizeCustomPageType(pageType, PAGE_TYPE_FACH) === PAGE_TYPE_BASIS
    ? 'Basisfragen'
    : 'Fachspezifische Fragen'
);

export const isConditionSource = (question) => {
  if (!question) return false;
  if (question.type === 'single-choice') return true;
  if (question.type === 'image' && question.answerType === 'single-choice') return true;
  if (question.type === 'combobox' && (question.comboboxMode || 'predefined') === 'predefined') return true;
  return false;
};

// For a combobox condition source, the selectable labels are its predefined
// entries (trimmed, non-empty). Non-combobox sources have no label dimension.
export const getConditionSourceLabels = (question) =>
  question?.type === 'combobox'
    ? (question.predefinedEntries || []).map((entry) => String(entry || '').trim()).filter(Boolean)
    : [];

// Normalizes a raw conditional config from the editor into the stored shape,
// or null when it is incomplete. Preserves requiresLabel for combobox sources.
export const sanitizeConditionalConfig = (config) => {
  if (!config?.dependsOn || !config?.requiresValue) return null;
  const next = { dependsOn: config.dependsOn, requiresValue: config.requiresValue };
  if (config.requiresLabel != null) next.requiresLabel = config.requiresLabel;
  return next;
};

const conditionalsAreSameScope = (a, b) => (a?.pageId || null) === (b?.pageId || null);

// Validates every question's `conditional` against its parent and repairs or
// drops it. A conditional survives only when its parent exists, is on the same
// page, is a valid condition source, and has options. For combobox parents,
// requiresLabel must be one of the predefined entries and requiresValue one of
// the options — stale values snap to the first valid entry.
export const normalizeQuestionConditionals = (sourceQuestions = []) => {
  const list = Array.isArray(sourceQuestions) ? sourceQuestions : [];
  const questionMap = new Map(list.map((question) => [String(question.id), question]));

  return list.map((question) => {
    if (!question?.conditional?.dependsOn) return question;

    const parent = questionMap.get(String(question.conditional.dependsOn));
    if (
      !parent ||
      String(parent.id) === String(question.id) ||
      !conditionalsAreSameScope(question, parent) ||
      !isConditionSource(parent)
    ) {
      const { conditional, ...rest } = question;
      return rest;
    }

    const validOptions = (parent.options || [])
      .map((option) => String(option || '').trim())
      .filter(Boolean);
    if (validOptions.length === 0) {
      const { conditional, ...rest } = question;
      return rest;
    }

    const nextRequiresValue = validOptions.includes(question.conditional.requiresValue)
      ? question.conditional.requiresValue
      : validOptions[0];

    if (parent.type === 'combobox') {
      const labels = getConditionSourceLabels(parent);
      if (labels.length === 0) {
        const { conditional, ...rest } = question;
        return rest;
      }
      const nextRequiresLabel = labels.includes(question.conditional.requiresLabel)
        ? question.conditional.requiresLabel
        : labels[0];
      return {
        ...question,
        conditional: { dependsOn: parent.id, requiresLabel: nextRequiresLabel, requiresValue: nextRequiresValue },
      };
    }

    return {
      ...question,
      conditional: { dependsOn: parent.id, requiresValue: nextRequiresValue },
    };
  });
};

export const clampDefaultQuestionsPageOrder = (order, customPageCount) => {
  const maxOrder = Math.max(1, Number(customPageCount) + 1);
  const numericOrder = Number(order);
  if (!Number.isFinite(numericOrder)) return maxOrder;
  return Math.min(Math.max(Math.round(numericOrder), 1), maxOrder);
};

export const createDefaultCustomPages = () => {
  return DEFAULT_CUSTOM_PAGE_PRESETS.map((preset, index) => {
    const page = createQuestionPage({ title: preset.title, order: index + 1, pageType: preset.pageType });
    return {
      id: page.id,
      title: page.title,
      description: page.description || '',
      order: index + 1,
      pageType: normalizeCustomPageType(page.pageType, inferCustomPageTypeByIndex(index))
    };
  });
};

export const parseSectionsLine = (line, fallbackTitle = '', fallbackDescription = '') => {
  const cleanLine = String(line || '').trim();
  if (!cleanLine) {
    return { title: fallbackTitle, description: fallbackDescription };
  }

  const separatorIndex = cleanLine.indexOf(':');
  if (separatorIndex === -1) {
    return { title: cleanLine || fallbackTitle, description: fallbackDescription };
  }

  const title = cleanLine.slice(0, separatorIndex).trim() || fallbackTitle;
  const description = cleanLine.slice(separatorIndex + 1).trim() || fallbackDescription;
  return { title, description };
};

export const buildSectionsLine = (title, description = '') => {
  const cleanTitle = String(title || '').trim();
  const cleanDescription = String(description || '').trim();
  if (!cleanTitle) return '';
  return cleanDescription ? `${cleanTitle}: ${cleanDescription}` : cleanTitle;
};

export const resolvePageTitle = (titleValue, fallbackTitle) => {
  return typeof titleValue === 'string' ? titleValue : fallbackTitle;
};

export const extractLegacySectionConfig = (sectionsText = '') => {
  const sectionLines = String(sectionsText || '')
    .split('\n')
    .map((line) => line.trim())
    .filter(Boolean);
  const defaultSectionLine = sectionLines[0] || DEFAULT_SECTION_LINES[0];
  const reviewSectionLine = sectionLines[sectionLines.length - 1] || DEFAULT_SECTION_LINES[2];
  const defaultSection = parseSectionsLine(defaultSectionLine, 'Rahmendaten & Qualifikationen', DEFAULT_SECTION_LINES[0].split(':')[1]?.trim() || '');
  const reviewSection = parseSectionsLine(reviewSectionLine, 'Überprüfung', DEFAULT_SECTION_LINES[2].split(':')[1]?.trim() || '');

  return {
    defaultSectionTitle: defaultSection.title || '',
    defaultSectionDescription: defaultSection.description || '',
    reviewSectionTitle: reviewSection.title || '',
    reviewSectionDescription: reviewSection.description || ''
  };
};

// Ensure each option within a single question carries a unique point value.
// Preserves the AI's quality ranking: the highest-scored option keeps its value,
// each subsequent (lower-ranked) option is bumped down by 1 if it collides with
// a higher-ranked one. When the range [0, maxScore] is too narrow to fit all
// options uniquely (e.g. 5 options with maxScore = 3), duplicates may remain at
// the bottom — that is mathematically unavoidable.
export const dedupeOptionScores = (scores, maxScore) => {
  if (!Array.isArray(scores) || scores.length <= 1) return scores;
  const ceiling = Math.max(0, Math.round(Number(maxScore) || 0));

  const indexed = scores.map((value, index) => {
    const numeric = Number(value);
    const safe = Number.isFinite(numeric) ? Math.max(0, Math.round(numeric)) : 0;
    return { score: Math.min(ceiling, safe), index };
  });

  indexed.sort((a, b) => b.score - a.score || a.index - b.index);

  for (let k = 1; k < indexed.length; k++) {
    if (indexed[k].score >= indexed[k - 1].score) {
      const target = indexed[k - 1].score - 1;
      if (target >= 0) indexed[k].score = target;
    }
  }

  const result = new Array(scores.length);
  indexed.forEach(({ score, index }) => { result[index] = score; });
  return result;
};
